import { Component, type ReactNode, type ErrorInfo } from 'react';
import i18next from 'i18next';
import { reportError } from '../../observability.js';

interface ErrorBoundaryProps {
  /** Optional fallback UI. If omitted the boundary renders the default
   *  fallback (verbose debug card in dev, friendly retry CTA in prod). */
  fallback?: ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  componentStack?: string;
}

/**
 * Resolve an i18n key with a hardcoded English fallback. The boundary is
 * a class component and may render before i18next finishes initialising
 * (boot-time error, paused tenant, etc.), so a `defaultValue` is the
 * safety net.
 */
function tWithFallback(key: string, fallback: string): string {
  try {
    if (i18next.isInitialized) {
      return i18next.t(key, { defaultValue: fallback });
    }
  } catch {
    // i18next can throw if the namespace isn't loaded — fall through.
  }
  return fallback;
}

/**
 * Generic React Error Boundary.
 *
 * Wraps a subtree and catches render-time errors so a single broken
 * widget/card doesn't tear down the whole app. Errors are routed through
 * `reportError` so they reach the same observability pipeline as the rest
 * of the bundle.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      scope: 'error-boundary',
      componentStack: info.componentStack,
    });
    // Under `exactOptionalPropertyTypes`, `componentStack: undefined` is
    // not assignable to optional `componentStack?: string`. Splat conditionally.
    const stack = info.componentStack ?? null;
    this.setState(stack !== null ? { componentStack: stack } : {});
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      const { error, componentStack } = this.state;
      const heading = tWithFallback('errorBoundary.title', 'Something went wrong');
      const retryLabel = tWithFallback('errorBoundary.retry', 'Try again');

      // Production: friendly card, no stack traces. The verbose debug
      // view leaks internals to public visitors and combines with any
      // upstream provider crash to surface a JS dump on the lobby. The
      // dev variant below preserves the original debug card.
      if (!import.meta.env.DEV) {
        return (
          <div
            role="alert"
            style={{
              padding: 24,
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              color: 'var(--text-primary)',
              background: 'var(--bg-surface)',
              maxWidth: 480,
              margin: '40px auto',
              border: '1px solid var(--bg-surface-subtle)',
              borderRadius: 8,
              textAlign: 'center',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{heading}</div>
            <button
              type="button"
              onClick={this.handleRetry}
              style={{
                marginTop: 12,
                padding: '8px 16px',
                background: 'var(--accent-primary)',
                color: 'var(--text-inverse)',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              {retryLabel}
            </button>
          </div>
        );
      }

      return (
        <div
          style={{
            padding: 24,
            fontFamily: 'ui-monospace, monospace',
            fontSize: 12,
            color: 'var(--text-primary)',
            background: 'var(--bg-surface)',
            maxWidth: 720,
            margin: '40px auto',
            border: '1px solid var(--status-negative)',
            borderLeft: '3px solid var(--status-negative)',
            borderRadius: 8,
          }}
        >
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, marginBottom: 8 }}>
            {tWithFallback('errorBoundary.heading', 'Render error')}
          </div>
          <div style={{ marginBottom: 12, color: 'var(--status-negative)' }}>
            {error?.name}: {error?.message}
          </div>
          {error?.stack && (
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
                color: 'var(--text-secondary)',
              }}
            >
              {error.stack}
            </pre>
          )}
          {componentStack && (
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                marginTop: 12,
                color: 'var(--text-muted)',
              }}
            >
              {componentStack}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
